import { z } from 'zod';
/** A link to another node's output: [nodeId, outputIndex]. */
export const WorkflowLinkSchema = z.tuple([z.string().min(1), z.number().int().min(0)]);
/** ComfyUI treats every list input as a link, so a list must be [nodeId, outputIndex]. */
const InputValueSchema = z.union([
    WorkflowLinkSchema, z.string(), z.number(), z.boolean(), z.null(), z.record(z.string(), z.unknown()),
], { error: 'A value is a string, number, boolean or a link [nodeId, outputIndex]' });
export const WorkflowNodeSchema = z.object({
    class_type: z.string().min(1).max(200),
    inputs: z.record(z.string(), InputValueSchema),
    _meta: z.object({ title: z.string().max(200).optional() }).loose().optional(),
}).loose();
export const MAX_WORKFLOW_NODES = 500;
export const MAX_WORKFLOW_BYTES = 512 * 1024;
/** A ComfyUI workflow in API format: { "<node id>": { class_type, inputs } }. */
export const WorkflowSchema = z.record(z.string().min(1).max(32), WorkflowNodeSchema)
    .refine((w) => Object.keys(w).length >= 1, 'A workflow needs at least one node')
    .refine((w) => Object.keys(w).length <= MAX_WORKFLOW_NODES, `At most ${MAX_WORKFLOW_NODES} nodes`)
    .refine((w) => JSON.stringify(w).length <= MAX_WORKFLOW_BYTES, 'The workflow is larger than 512 KB');
export const RecipeAuthorSchema = z.enum(['human', 'ai']);
export const RecipeSchema = z.object({
    id: z.string(),
    title: z.string(),
    intent: z.string().nullable(),
    family: z.string().nullable(),
    notes: z.string().nullable(),
    author: RecipeAuthorSchema,
    machineId: z.string().nullable(),
    version: z.number().int(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const RecipeVersionSchema = z.object({
    version: z.number().int(),
    author: RecipeAuthorSchema,
    note: z.string().nullable(),
    createdAt: z.string(),
});
const text = (max) => z.string().trim().max(max);
export const CreateRecipeRequestSchema = z.object({
    title: text(120).min(1, 'Required'),
    intent: text(2000).optional(),
    family: text(40).optional(),
    notes: text(4000).optional(),
    machineId: z.string().min(1).max(64).optional(),
    workflow: WorkflowSchema,
});
export const UpdateRecipeRequestSchema = z.object({
    title: text(120).min(1).optional(),
    intent: text(2000).optional(),
    family: text(40).optional(),
    notes: text(4000).optional(),
    machineId: z.string().min(1).max(64).nullable().optional(),
    workflow: WorkflowSchema.optional(),
    note: text(500).optional(),
});
export const RecipeResponseSchema = z.object({ recipe: RecipeSchema });
export const RecipesResponseSchema = z.object({ recipes: z.array(RecipeSchema) });
export const RecipeDetailResponseSchema = z.object({ recipe: RecipeSchema, workflow: WorkflowSchema, versions: z.array(RecipeVersionSchema) });
/** A topic of the knowledge base (doc 44): "general" or a file's id under knowledge/. */
export const GuideTopicSchema = z.string().regex(/^[a-z0-9][a-z0-9-]{0,39}$/, 'A topic id from the guide\'s list');
export const GuideTopicInfoSchema = z.object({ id: z.string(), title: z.string(), summary: z.string(), lastVerified: z.string() });
export const RecipeGuideResponseSchema = z.object({ topics: z.array(GuideTopicInfoSchema), guide: z.string() });
/** The Knowledge tab (doc 44): the same topics for people, grouped by their folder. */
export const KnowledgeGroupSchema = z.enum(['start', 'models', 'techniques', 'comfyui', 'media']);
export const KnowledgeTopicInfoSchema = GuideTopicInfoSchema.extend({ group: KnowledgeGroupSchema });
export const KnowledgeListResponseSchema = z.object({ topics: z.array(KnowledgeTopicInfoSchema) });
export const KnowledgeTopicResponseSchema = z.object({ topic: KnowledgeTopicInfoSchema.extend({ path: z.string(), body: z.string() }) });
//# sourceMappingURL=recipes.js.map